import { motion } from 'framer-motion';
import { Bell, Calendar, Coffee, MessageSquare, Slack } from 'lucide-react';

const lines = [
  'We remember every meeting.',
  'Every deadline.',
  'Every notification.',
  'But somehow forget',
  'the simplest thing.',
];

export default function Problem() {
  return (
    <section
      id="problem"
      className="relative min-h-[100svh] w-full bg-[#0E0E0E] text-[#FAF8F4] overflow-hidden"
    >
      {/* Background photo of cluttered desk */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'url(https://images.pexels.com/photos/4841696/pexels-photo-4841696.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(100%) contrast(1.05)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0E] via-[#0E0E0E]/70 to-[#0E0E0E]" />

      {/* Floating chaos icons */}
      <div className="absolute inset-0 pointer-events-none">
        {[
          { Icon: Slack, top: '14%', left: '8%', delay: 0 },
          { Icon: Calendar, top: '22%', right: '12%', delay: 0.4 },
          { Icon: Bell, top: '62%', left: '6%', delay: 0.8 },
          { Icon: Coffee, top: '70%', right: '10%', delay: 1.2 },
          { Icon: MessageSquare, top: '40%', right: '6%', delay: 1.6 },
        ].map(({ Icon, delay, ...pos }, i) => (
          <motion.div
            key={i}
            style={pos}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 0.5, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1, delay }}
            className="absolute hidden md:block text-[#CBBBA0]"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5 + i, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Icon size={26} strokeWidth={1.2} />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Floating notification cards */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        {[
          { top: '18%', left: '14%', label: 'Daily standup · 9:00', rot: -3, delay: 0.2 },
          { top: '60%', left: '10%', label: 'Slack · 12 new threads', rot: 2, delay: 0.6 },
          { top: '24%', right: '14%', label: 'Cal · 7 meetings today', rot: 4, delay: 1 },
          { top: '68%', right: '12%', label: 'Inbox · 38 unread', rot: -2, delay: 1.4 },
        ].map((c, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30, rotate: c.rot }}
            whileInView={{ opacity: 1, y: 0, rotate: c.rot }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: c.delay }}
            style={{ top: c.top, left: c.left, right: c.right }}
            className="absolute bg-white/5 backdrop-blur-md border border-white/10 px-4 py-2.5 rounded-md text-[11px] tracking-wide text-white/70"
          >
            {c.label}
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 min-h-[100svh] flex flex-col items-center justify-center px-6 py-32 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-8"
        >
          Chapter 01 · The Problem
        </motion.div>

        <div className="max-w-4xl space-y-3">
          {lines.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-[clamp(2rem,5.5vw,4.5rem)] leading-[1.05] tracking-tight text-[#FAF8F4]"
            >
              {line}
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.9 }}
            className="pt-6 font-serif italic text-[clamp(2.5rem,7vw,6rem)] leading-none text-[#CBBBA0]"
          >
            Ourselves.
          </motion.div>
        </div>
      </div>
    </section>
  );
}
